import React, {useState, useEffect, useMemo} from 'react';
import {collection, getDocs, doc, getDoc, updateDoc, query, where, addDoc, Timestamp} from 'firebase/firestore';
import {firestoreProd, firestoreDev, authProd, authDev} from '../../firebase';
import {
    FaApple,
    FaBan,
    FaBuilding,
    FaChevronDown,
    FaChevronRight,
    FaDatabase,
    FaDollarSign,
    FaEnvelope,
    FaGoogle,
    FaMicrosoft,
    FaQuestionCircle,
    FaSearch,
    FaUser,
    FaUserPlus,
} from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import EnterpriseBillingDialog from './EnterpriseBillingDialog';
import { isEnterpriseGrowthOrg } from './enterpriseBillingOptions';
import { formatSignedUpDate } from './resolveSignedUpAt';
import { mainApiUrl, mainApiUrlDev } from '../../globalVariables';

// Available roles for organization members (Addy AI Permissions)
// Based on the comprehensive permission system
const MEMBER_ROLES = [
    { name: "Admin", value: "Admin", description: "Full access to everything" },
    { name: "Processor", value: "Processor", description: "Can manage loans and contacts" },
    { name: "Loan Officer", value: "Loan Officer", description: "Can manage loans, contacts, and view AI agents" },
    { name: "Loan Officer Assistant", value: "Loan Officer Assistant", description: "Can view loans and contacts, edit own items" },
    { name: "Closer", value: "Closer", description: "Can view and edit loans in closing stage" },
    // Legacy roles for backward compatibility
    { name: "Can Edit (Legacy)", value: "edit", description: "Legacy role - similar to Processor" },
    { name: "Can View (Legacy)", value: "view", description: "Legacy role - view only access" },
];

// Partner options that map directly to PromoteKit referral code slugs.
// The slug is what gets written to promotekitReferralCode in Firestore —
// identical to what the organic ?via= link flow writes, so DB queries return both.
// "__custom__" is a sentinel value that shows a free-text input (always lowercased before save).
const PARTNER_OPTIONS = [
    { label: "None",   slug: "" },
    { label: "Willow", slug: "willow" },
    { label: "C2",     slug: "c2" },
    { label: "Custom", slug: "__custom__" },
];
const CUSTOM_PARTNER_SENTINEL = "__custom__";

// Bootstrap badge classes / labels for LOS integration connection states.
const INTEGRATION_STATUS_BADGE = {
    approved: "bg-success",
    waiting_approval: "bg-warning text-dark",
    rejected: "bg-danger",
    disabled: "bg-secondary",
};
const INTEGRATION_STATUS_LABEL = {
    approved: "Connected",
    waiting_approval: "Waiting approval",
    rejected: "Rejected",
    disabled: "Disabled",
};

/**
 * Derive the LOS integrations an organization is connected to (or in the
 * process of connecting) from the nested fields already stored on the org
 * document. Only integrations with any presence are returned. Encompass covers
 * both the legacy `encompass` and modern `encompassEPC` shapes.
 * @param {Object} org - Organization document data
 * @returns {Array<{ key: string, label: string, status: string }>}
 */
function getConnectedIntegrations(org) {
    if (!org) {
        return [];
    }

    const integrations = [];

    const meridianlink = org.meridianlink;
    if (meridianlink && (meridianlink.enabled || meridianlink.status)) {
        const status =
            meridianlink.status === "approved" || meridianlink.enabled === true
                ? "approved"
                : meridianlink.status || "waiting_approval";
        integrations.push({ key: "meridianlink", label: "MeridianLink", status });
    }

    const encompassConnected =
        org.encompassEPC?.enabled === true || org.encompass?.enabled === true;
    const encompassConfigured = Boolean(
        org.encompassEPC?.clientId || org.encompass?.clientId
    );
    if (encompassConnected || encompassConfigured) {
        integrations.push({
            key: "encompass",
            label: "Encompass",
            status: encompassConnected ? "approved" : "waiting_approval",
        });
    }

    const arive = org.arive;
    if (arive && (arive.enabled || arive.apiKey || arive.baseUrl || arive.clientId)) {
        integrations.push({
            key: "arive",
            label: "Arive",
            status: arive.enabled === true ? "approved" : "waiting_approval",
        });
    }

    const lendingpad = org.lendingpad;
    if (lendingpad && (lendingpad.status || lendingpad.company || lendingpad.enabled !== undefined)) {
        let status = lendingpad.status || "waiting_approval";
        if (lendingpad.status === "approved" && lendingpad.enabled === false) {
            status = "disabled";
        }
        integrations.push({ key: "lendingpad", label: "LendingPad", status });
    }

    return integrations;
}

/** How the filter box interprets the search term (client filter + optional Firestore lookup). */
const USER_SEARCH_MODES = [
    { value: "all", label: "All fields", placeholder: "Email, name, user ID, org ID, org name…" },
    { value: "email", label: "Email", placeholder: "Email address (substring or exact for lookup)" },
    { value: "userId", label: "User ID", placeholder: "Firebase user / document ID" },
    { value: "orgId", label: "Organization ID", placeholder: "Organization document ID" },
    { value: "orgName", label: "Organization name", placeholder: "Org name (substring on loaded rows; exact match also queries Firestore)" },
];

const AUTH_PROVIDER_META = {
    google: { label: "Google", icon: FaGoogle },
    microsoft: { label: "Microsoft", icon: FaMicrosoft },
    apple: { label: "Apple", icon: FaApple },
    credentials: { label: "Email & password", icon: FaEnvelope },
    unknown: { label: "Unknown", icon: FaQuestionCircle },
};

const AUTH_PROVIDER_FILTER_OPTIONS = [
    { value: "all", label: "All sign-in methods" },
    ...Object.entries(AUTH_PROVIDER_META).map(([value, meta]) => ({
        value,
        label: meta.label,
    })),
];

function getAuthProviderKey(provider) {
    const normalizedProvider = String(provider || "").trim().toLowerCase();
    const providerAliases = {
        "microsoft-entra-id": "microsoft",
        "azure-ad": "microsoft",
    };
    const providerKey = providerAliases[normalizedProvider] || normalizedProvider;

    return AUTH_PROVIDER_META[providerKey] ? providerKey : "unknown";
}

function AuthProviderBadge({ provider }) {
    const providerKey = getAuthProviderKey(provider);
    const { label, icon: ProviderIcon } = AUTH_PROVIDER_META[providerKey];
    const title = provider
        ? `Last recorded sign-in method: ${label}`
        : "No last sign-in method recorded";

    return (
        <span
            className="badge bg-light text-dark border d-inline-flex align-items-center gap-1"
            title={title}
            aria-label={title}
        >
            <ProviderIcon aria-hidden="true" />
            {label}
        </span>
    );
}

// Invite Member Dialog Component
const InviteMemberDialog = ({ isOpen, onClose, organizationId, organizationName, onSuccess, firestore, useDevDb }) => {
    const [email, setEmail] = useState('');
    const [role, setRole] = useState('Loan Officer'); // Default to "Loan Officer"
    const [isAddy, setIsAddy] = useState(false);
    // Default OFF: invite follows the normal flow (pending status + emailed accept link).
    // When ON the member is written directly with "accepted" status and gets no email.
    const [autoAccept, setAutoAccept] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrors({});

        // Validate email
        if (!email.trim()) {
            setErrors({ email: 'Email is required' });
            return;
        }
        if (!validateEmail(email)) {
            setErrors({ email: 'Please enter a valid email address' });
            return;
        }

        setIsSubmitting(true);

        const normalizedEmail = email.trim().toLowerCase();

        try {
            if (autoAccept) {
                // Auto-accept: write the member directly with "accepted" status. No email is
                // sent and the member has immediate access (legacy admin-dashboard behavior).
                const membersRef = collection(firestore, 'organization-members');
                await addDoc(membersRef, {
                    acceptCode: null,
                    createdAt: Timestamp.now(),
                    email: normalizedEmail,
                    isAddy: isAddy,
                    organizationId: organizationId,
                    role: role,
                    status: 'accepted',
                    userId: null, // Will be set when user logs in
                });
                toast.success(`Added ${normalizedEmail} to ${organizationName} with immediate access`);
            } else {
                // Normal flow: go through the backend so the invite is created as "pending"
                // and the invitee receives the accept-link email.
                const activeMainApiUrl = useDevDb ? mainApiUrlDev : mainApiUrl;
                const adminUid = (useDevDb ? authDev : authProd)?.currentUser?.uid;
                if (!adminUid) {
                    throw new Error('You must be signed in to send an invitation email');
                }
                const resp = await fetch(
                    `${activeMainApiUrl}/organization/${organizationId}/invite-member?uid=${encodeURIComponent(adminUid)}`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            email: normalizedEmail,
                            role: role,
                            isAddy: isAddy,
                        }),
                    },
                );
                if (!resp.ok) {
                    let reason = `Request failed (${resp.status})`;
                    try {
                        const data = await resp.json();
                        reason = data?.message || reason;
                    } catch (_) { /* response had no JSON body */ }
                    throw new Error(reason);
                }
                toast.success(`Invitation email sent to ${normalizedEmail} (pending)`);
            }

            // Reset form
            setEmail('');
            setRole('Loan Officer');
            setIsAddy(false);
            setAutoAccept(false);

            // Call success callback
            if (onSuccess) {
                onSuccess();
            }

            onClose();
        } catch (error) {
            console.error('Error inviting member:', error);
            toast.error(error.message || 'Failed to invite member. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setEmail('');
        setRole('Loan Officer');
        setIsAddy(false);
        setAutoAccept(false);
        setErrors({});
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            <FaUserPlus className="me-2" />
                            Invite New Member
                        </h5>
                        <button 
                            type="button" 
                            className="btn-close" 
                            onClick={handleClose}
                            disabled={isSubmitting}
                        ></button>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                            <p className="text-muted mb-4">
                                Invite a new member to <strong>{organizationName}</strong>
                            </p>

                            {/* Email Field */}
                            <div className="mb-3">
                                <label htmlFor="memberEmail" className="form-label">
                                    Email Address <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="email"
                                    className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                    id="memberEmail"
                                    placeholder="Enter email address"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    disabled={isSubmitting}
                                    autoFocus
                                />
                                {errors.email && (
                                    <div className="invalid-feedback">{errors.email}</div>
                                )}
                            </div>

                            {/* Role Field */}
                            <div className="mb-3">
                                <label htmlFor="memberRole" className="form-label">
                                    Role
                                </label>
                                <select
                                    className="form-select"
                                    id="memberRole"
                                    value={role}
                                    onChange={(e) => setRole(e.target.value)}
                                    disabled={isSubmitting}
                                >
                                    {MEMBER_ROLES.map((roleOption) => (
                                        <option key={roleOption.value} value={roleOption.value}>
                                            {roleOption.name}
                                        </option>
                                    ))}
                                </select>
                                {/* Show description for selected role */}
                                <small className="text-muted">
                                    {MEMBER_ROLES.find(r => r.value === role)?.description || 'Select a role'}
                                </small>
                            </div>

                            {/* Is Addy Checkbox */}
                            <div className="mb-3">
                                <div className="form-check">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id="isAddyCheckbox"
                                        checked={isAddy}
                                        onChange={(e) => setIsAddy(e.target.checked)}
                                        disabled={isSubmitting}
                                    />
                                    <label className="form-check-label" htmlFor="isAddyCheckbox">
                                        Is Addy Team Member
                                    </label>
                                </div>
                                <small className="text-muted">
                                    Check this if the member is part of the Addy AI team
                                </small>
                            </div>

                            {/* Auto-accept Checkbox */}
                            <div className="mb-3">
                                <div className="form-check">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id="autoAcceptCheckbox"
                                        checked={autoAccept}
                                        onChange={(e) => setAutoAccept(e.target.checked)}
                                        disabled={isSubmitting}
                                    />
                                    <label className="form-check-label" htmlFor="autoAcceptCheckbox">
                                        Auto-accept invitation
                                    </label>
                                </div>
                                <small className="text-muted">
                                    Skip the email and grant immediate access. Leave unchecked to send a
                                    normal invitation email with a pending status.
                                </small>
                            </div>

                            {/* Info Alert */}
                            <div className={`alert ${autoAccept ? 'alert-warning' : 'alert-info'} mb-0`} role="alert">
                                <small>
                                    {autoAccept ? (
                                        <>
                                            <strong>Note:</strong> The member will be added with "accepted"
                                            status and will have immediate access — no email is sent.
                                        </>
                                    ) : (
                                        <>
                                            <strong>Note:</strong> An invitation email will be sent and the
                                            member stays "pending" until they accept it.
                                        </>
                                    )}
                                </small>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button 
                                type="button" 
                                className="btn btn-secondary" 
                                onClick={handleClose}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit" 
                                className="btn btn-primary"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                        Inviting...
                                    </>
                                ) : (
                                    <>
                                        <FaUserPlus className="me-2" />
                                        Invite Member
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

/**
 * Plan tier for an organization subscription (mirrors EnterpriseOrgs / leadConversionCacheRefresh).
 * @param {object} subscription - org.subscription object
 * @returns {"FREE"|"ESSENTIALS"|"GROWTH"|"ENTERPRISE"}
 */
const getPlanTier = (subscription) => {
    if (!subscription || subscription.status !== 'active') return 'FREE';
    const p = subscription.priceId || '';
    if (p.includes('enterprise')) return 'ENTERPRISE';
    if (p.includes('growth')) return 'GROWTH';
    if (p.includes('essentials')) return 'ESSENTIALS';
    if (p.startsWith('price_')) return 'GROWTH';
    return 'FREE';
};

/**
 * Confirmation modal for deactivating an organization.
 *
 * Deactivating sets `active: false` on organizations/{orgId} — the backend treats
 * `active !== false` as active and hides deactivated orgs from the user's org switcher.
 * To guard against mistakes the admin must type "{realLoans}-{PLAN}" (e.g. "0-FREE"),
 * where realLoans is the loan count minus the auto-created test loan that every org ships with.
 */
const DeactivateOrgModal = ({ isOpen, onClose, orgId, orgName, loanCount, subscription, onConfirm, isDeactivating }) => {
    const [confirmText, setConfirmText] = useState('');

    useEffect(() => {
        if (isOpen) setConfirmText('');
    }, [isOpen, orgId]);

    if (!isOpen) return null;

    const plan = getPlanTier(subscription);
    const realLoans = Math.max(0, (Number(loanCount) || 0) - 1);
    const token = `${realLoans}-${plan}`;
    const canConfirm = !isDeactivating && confirmText.trim().toUpperCase() === token.toUpperCase();

    return (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title text-danger">
                            <FaBan className="me-2" />
                            Deactivate Organization
                        </h5>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={onClose}
                            disabled={isDeactivating}
                        ></button>
                    </div>
                    <div className="modal-body">
                        <ul className="list-unstyled mb-3">
                            <li><strong>Organization:</strong> {orgName || <span className="text-muted">Unnamed</span>}</li>
                            <li><strong>Org ID:</strong> <small className="text-muted">{orgId}</small></li>
                            <li><strong>Plan:</strong> {plan}</li>
                            <li><strong>Loans (excl. test loan):</strong> {realLoans}</li>
                        </ul>
                        <div className="alert alert-danger" role="alert">
                            <small>
                                This sets <code>active: false</code> on the organization. Members will no
                                longer see it when switching organizations. Type{' '}
                                <strong>{token}</strong> below to confirm.
                            </small>
                        </div>
                        <label className="form-label">
                            Type <code>{token}</code> to confirm (loans-plan)
                        </label>
                        <input
                            type="text"
                            className="form-control"
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && canConfirm) onConfirm();
                            }}
                            placeholder={token}
                            disabled={isDeactivating}
                            autoFocus
                            style={{ fontFamily: 'monospace' }}
                        />
                    </div>
                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={onClose}
                            disabled={isDeactivating}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-danger"
                            onClick={onConfirm}
                            disabled={!canConfirm}
                        >
                            {isDeactivating ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                    Deactivating...
                                </>
                            ) : (
                                <>
                                    <FaBan className="me-2" />
                                    Deactivate
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Add some CSS for the accordion
const accordionStyles = `
    .accordion-button:focus {
        box-shadow: none;
    }
    .accordion-arrow {
        transition: transform 0.2s ease;
    }
    .accordion-button[aria-expanded="true"] .accordion-arrow {
        transform: rotate(180deg);
    }
    .accordion-button {
        color: #495057 !important;
    }
    .accordion-button:hover {
        background-color: #f8f9fa;
    }
`;

const UserPermissionsPage = () => {
    const [allUsers, setAllUsers] = useState([]);
    const [isLoadingAllUsers, setIsLoadingAllUsers] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [updatingUserId, setUpdatingUserId] = useState(null);
    const [error, setError] = useState(null);
    const [hasLoadedAllUsers, setHasLoadedAllUsers] = useState(false);
    const [expandedUsers, setExpandedUsers] = useState(new Set());
    const [loadingUserDetails, setLoadingUserDetails] = useState(new Set());
    const [tableSearchTerm, setTableSearchTerm] = useState('');
    const [userSearchMode, setUserSearchMode] = useState('all');
    const [authProviderFilter, setAuthProviderFilter] = useState('all');
    
    // Database Environment State - default to prod
    const [useDevDb, setUseDevDb] = useState(false);
    
    // Get the active firestore instance based on toggle
    const activeFirestore = useMemo(() => useDevDb ? firestoreDev : firestoreProd, [useDevDb]);
    
    // Invite Member Dialog State
    const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
    const [inviteDialogOrgId, setInviteDialogOrgId] = useState(null);
    const [inviteDialogOrgName, setInviteDialogOrgName] = useState('');
    
    // Organization Members State
    const [organizationMembers, setOrganizationMembers] = useState({});
    const [loadingMembers, setLoadingMembers] = useState(new Set());
    // partnerSlugByUserId: maps userId → selected partner slug (e.g. "willow", "c2", "__custom__", or "")
    const [partnerSlugByUserId, setPartnerSlugByUserId] = useState({});
    // customPartnerSlugByUserId: maps userId → free-text value when "__custom__" is selected
    const [customPartnerSlugByUserId, setCustomPartnerSlugByUserId] = useState({});
    const [savingReferralUserId, setSavingReferralUserId] = useState(null);

    // Enterprise Billing Dialog State
    const [billingDialogOpen, setBillingDialogOpen] = useState(false);
    const [billingDialogUser, setBillingDialogUser] = useState(null);
    const [activationDialogUser, setActivationDialogUser] = useState(null);

    // Forward Deployed AI Engineer (FDE) roster (from the `fdes` collection).
    // Growth + Enterprise orgs each carry an `assignedFde`; this list powers the
    // reassignment dropdown in the expanded org details.
    const [fdeRoster, setFdeRoster] = useState([]);
    const [updatingFdeOrgId, setUpdatingFdeOrgId] = useState(null);

    // Track which users have the deactivated org panel expanded
    const [showDeactivatedOrgFor, setShowDeactivatedOrgFor] = useState(new Set());

    // Deactivate Organization modal state
    // deactivateOrgInfo: { orgId, orgName, loanCount, subscription } | null
    const [deactivateOrgInfo, setDeactivateOrgInfo] = useState(null);
    const [isDeactivatingOrg, setIsDeactivatingOrg] = useState(false);

    /**
     * Soft-deactivate an organization by setting active:false on its doc.
     * Updates local state so every row referencing the org reflects the change.
     */
    const handleDeactivateOrg = async () => {
        if (!deactivateOrgInfo?.orgId) return;
        const oid = deactivateOrgInfo.orgId;
        setIsDeactivatingOrg(true);
        try {
            const orgRef = doc(activeFirestore, 'organizations', oid);
            const by = (useDevDb ? authDev : authProd)?.currentUser?.email || 'unknown';
            await updateDoc(orgRef, {
                active: false,
                deactivatedAt: Timestamp.now(),
                deactivatedBy: by,
            });

            setAllUsers(prev =>
                prev.map(u => {
                    let od = u.organizationData;
                    if (!od) return u;
                    let changed = false;
                    if (od.id === oid) {
                        od = { ...od, active: false };
                        changed = true;
                    }
                    if (Array.isArray(od._allMemberships) && od._allMemberships.some(m => m.orgId === oid)) {
                        od = {
                            ...od,
                            _allMemberships: od._allMemberships.map(m =>
                                m.orgId === oid ? { ...m, orgActive: false } : m
                            ),
                        };
                        changed = true;
                    }
                    return changed ? { ...u, organizationData: od } : u;
                })
            );

            toast.success(`Deactivated ${deactivateOrgInfo.orgName || oid}`);
            setDeactivateOrgInfo(null);
        } catch (error) {
            console.error('Error deactivating organization:', error);
            toast.error('Failed to deactivate organization');
        } finally {
            setIsDeactivatingOrg(false);
        }
    };

    const toggleDeactivatedOrg = (userId, e) => {
        e.stopPropagation();
        setShowDeactivatedOrgFor(prev => {
            const next = new Set(prev);
            next.has(userId) ? next.delete(userId) : next.add(userId);
            return next;
        });
    };

    /**
     * Returns the org to display as "primary" for a user.
     * If the primary org is deactivated and there's an active invited org, return the first active invited one.
     * Also returns the deactivated org separately if it was swapped out.
     */
    const getDisplayOrg = (user) => {
        const primaryOrg = user.organizationData;
        const isPrimaryDeactivated = primaryOrg?.id && primaryOrg.active === false;
        if (!isPrimaryDeactivated) {
            return { displayOrg: primaryOrg, deactivatedOrg: null };
        }
        const firstActiveInvited = primaryOrg._allMemberships?.find(m => m.orgActive);
        if (!firstActiveInvited) {
            return { displayOrg: primaryOrg, deactivatedOrg: null };
        }
        // Build a lightweight display org from the membership record
        const swappedOrg = {
            id: firstActiveInvited.orgId,
            name: firstActiveInvited.orgName,
            active: true,
            subscription: firstActiveInvited.orgSubscription,
            billing: firstActiveInvited.orgBilling || undefined,
            stripeCustomerId: firstActiveInvited.orgStripeCustomerId || undefined,
            whiteLabelSettings: firstActiveInvited.orgWhiteLabelSettings || undefined,
            assignedFde: firstActiveInvited.orgAssignedFde || null,
            isMember: true,
            memberRole: firstActiveInvited.role,
            _allMemberships: primaryOrg._allMemberships,
            _isSwappedFromDeactivated: true,
        };
        return { displayOrg: swappedOrg, deactivatedOrg: primaryOrg };
    };

    // Direct Firestore lookups (email, user id, org id, org name) for users not in the main list
    const [emailSearchResults, setEmailSearchResults] = useState([]);
    const [isSearchingByEmail, setIsSearchingByEmail] = useState(false);

    const searchModeMeta = USER_SEARCH_MODES.find((m) => m.value === userSearchMode) || USER_SEARCH_MODES[0];

    // Compute filtered users based on search input (from the main allUsers list)
    const filteredUsers = useMemo(() => {
        const usersMatchingAuthProvider = authProviderFilter === "all"
            ? allUsers
            : allUsers.filter(
                (user) => getAuthProviderKey(user.lastAuthProvider) === authProviderFilter
            );

        if (!tableSearchTerm.trim()) {
            return usersMatchingAuthProvider;
        }

        const searchLower = tableSearchTerm.toLowerCase().trim();

        return usersMatchingAuthProvider.filter((user) => {
            switch (userSearchMode) {
                case "email":
                    return user.email?.toLowerCase().includes(searchLower);
                case "userId":
                    return user.id?.toLowerCase().includes(searchLower);
                case "orgId": {
                    const orgRefId = (user.organization && String(user.organization)) || "";
                    return (
                        user.organizationData?.id?.toLowerCase().includes(searchLower) ||
                        orgRefId.toLowerCase().includes(searchLower)
                    );
                }
                case "orgName":
                    return user.organizationData?.name?.toLowerCase().includes(searchLower);
                case "all":
                default: {
                    const emailMatch = user.email?.toLowerCase().includes(searchLower);
                    const nameMatch = user.name?.toLowerCase().includes(searchLower);
                    const orgMatch = user.organizationData?.name?.toLowerCase().includes(searchLower);
                    const orgIdMatch = user.organizationData?.id?.toLowerCase().includes(searchLower);
                    const userIdMatch = user.id?.toLowerCase().includes(searchLower);
                    const orgDefaultForUserIdMatch = user.organizationData?.defaultForUserId
                        ?.toLowerCase()
                        .includes(searchLower);
                    const orgRefMatch = user.organization
                        ? String(user.organization).toLowerCase().includes(searchLower)
                        : false;
                    return (
                        emailMatch ||
                        nameMatch ||
                        orgMatch ||
                        orgIdMatch ||
                        userIdMatch ||
                        orgDefaultForUserIdMatch ||
                        orgRefMatch
                    );
                }
            }
        });
    }, [allUsers, tableSearchTerm, userSearchMode, authProviderFilter]);

    /**
     * Firestore-backed search by mode: finds user docs not necessarily in `createdAccountOnClient` list.
     */
    useEffect(() => {
        const raw = tableSearchTerm.trim();
        if (!raw || !activeFirestore) {
            setEmailSearchResults([]);
            return;
        }

        const termLower = raw.toLowerCase();
        const looksLikeEmail =
            termLower.length >= 5 &&
            termLower.includes("@") &&
            /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(termLower);

        const looksLikeFirestoreId = (s) => /^[A-Za-z0-9_-]+$/.test(s);

        const shouldRunEmailLookup =
            (userSearchMode === "all" || userSearchMode === "email") && looksLikeEmail;
        // Avoid getDoc spam in "all" mode: only treat as user id when string resembles a Firebase uid.
        const shouldRunUserIdLookup =
            userSearchMode === "userId"
                ? raw.length >= 10 && looksLikeFirestoreId(raw)
                : userSearchMode === "all" && raw.length >= 20 && looksLikeFirestoreId(raw);
        const shouldRunOrgIdLookup =
            userSearchMode === "orgId"
                ? raw.length > 0 && looksLikeFirestoreId(raw)
                : userSearchMode === "all" && raw.length >= 16 && looksLikeFirestoreId(raw);
        const shouldRunOrgNameLookup = userSearchMode === "orgName";

        if (!shouldRunEmailLookup && !shouldRunUserIdLookup && !shouldRunOrgIdLookup && !shouldRunOrgNameLookup) {
            setEmailSearchResults([]);
            return;
        }

        let cancelled = false;
        setIsSearchingByEmail(true);

        const userDocToRow = (docSnapshot) => {
            const userData = docSnapshot.data();
            return {
                id: docSnapshot.id,
                ...userData,
                organizationData: null,
                organizationLoaded: false,
            };
        };

        const fetchUsersForOrgDoc = async (orgDocSnap) => {
            if (!orgDocSnap.exists()) return [];
            const orgData = orgDocSnap.data();
            const ownerId = orgData.defaultForUserId;
            if (!ownerId) return [];
            const userSnap = await getDoc(doc(activeFirestore, "users", ownerId));
            if (!userSnap.exists()) return [];
            return [userDocToRow(userSnap)];
        };

        const runDirectSearch = async () => {
            const byId = new Map();
            const addUsers = (arr) => {
                for (const u of arr) {
                    if (u?.id) byId.set(u.id, u);
                }
            };

            try {
                if (shouldRunEmailLookup) {
                    const usersRef = collection(activeFirestore, "users");
                    const q = query(usersRef, where("email", "==", termLower));
                    const snapshot = await getDocs(q);
                    if (cancelled) return;
                    addUsers(snapshot.docs.map(userDocToRow));
                }

                if (shouldRunUserIdLookup) {
                    const userSnap = await getDoc(doc(activeFirestore, "users", raw));
                    if (cancelled) return;
                    if (userSnap.exists()) addUsers([userDocToRow(userSnap)]);
                }

                if (shouldRunOrgIdLookup) {
                    const orgSnap = await getDoc(doc(activeFirestore, "organizations", raw));
                    if (cancelled) return;
                    if (orgSnap.exists()) {
                        addUsers(await fetchUsersForOrgDoc(orgSnap));
                    }
                }

                if (shouldRunOrgNameLookup) {
                    const orgsRef = collection(activeFirestore, "organizations");
                    const nameQuery = query(orgsRef, where("name", "==", raw));
                    const nameSnap = await getDocs(nameQuery);
                    if (cancelled) return;
                    for (const orgDoc of nameSnap.docs) {
                        addUsers(await fetchUsersForOrgDoc(orgDoc));
                    }
                }

                const users = Array.from(byId.values());
                users.sort((a, b) => (a.email || "").localeCompare(b.email || ""));
                if (!cancelled) setEmailSearchResults(users);
            } catch (err) {
                if (!cancelled) {
                    console.error("Direct user search failed:", err);
                    setEmailSearchResults([]);
                }
            } finally {
                if (!cancelled) setIsSearchingByEmail(false);
            }
        };

        runDirectSearch();
        return () => {
            cancelled = true;
        };
    }, [tableSearchTerm, activeFirestore, userSearchMode]);

    // Merged list: filtered users from main load + any users found by exact email search (dedupe by id)
    const displayedUsers = useMemo(() => {
        const idsFromFiltered = new Set(filteredUsers.map(u => u.id));
        const extraFromEmailSearch = emailSearchResults.filter((user) => {
            const isNewResult = !idsFromFiltered.has(user.id);
            const matchesAuthProvider =
                authProviderFilter === "all" ||
                getAuthProviderKey(user.lastAuthProvider) === authProviderFilter;

            return isNewResult && matchesAuthProvider;
        });

        return [...filteredUsers, ...extraFromEmailSearch];
    }, [filteredUsers, emailSearchResults, authProviderFilter]);

    // Load all users on component mount and when database changes
    useEffect(() => {
        // Reset state when database changes
        setAllUsers([]);
        setExpandedUsers(new Set());
        setOrganizationMembers({});
        setEmailSearchResults([]);
        setHasLoadedAllUsers(false);
        fetchAllUsersBasic();
        fetchFdeRoster();
    }, [useDevDb]);

    // Load the forward-deployed engineer roster for the active database.
    const fetchFdeRoster = async () => {
        if (!activeFirestore) return;
        try {
            const snapshot = await getDocs(collection(activeFirestore, 'fdes'));
            const roster = snapshot.docs
                .map((d) => {
                    const data = d.data() || {};
                    const email = (data.email || '').trim().toLowerCase();
                    if (!email) return null;
                    const firstName = data.firstname || data.firstName || '';
                    const lastName = data.lastname || data.lastName || '';
                    return {
                        id: d.id,
                        email,
                        firstName,
                        lastName,
                        fullName: `${firstName} ${lastName}`.trim() || email,
                    };
                })
                .filter(Boolean)
                .sort((a, b) => a.fullName.localeCompare(b.fullName));
            setFdeRoster(roster);
        } catch (error) {
            console.warn('Error loading FDE roster:', error);
            setFdeRoster([]);
        }
    };

    // Assign / reassign a forward-deployed engineer to an organization. Writes
    // the denormalized `assignedFde` record directly on the org doc (matching
    // this page's direct-Firestore pattern) and mirrors it into local state.
    const handleAssignFde = async (user, orgId, fdeEmail) => {
        if (!orgId) return;
        setUpdatingFdeOrgId(orgId);
        try {
            let assignedFde = null;
            if (fdeEmail) {
                const fde = fdeRoster.find((f) => f.email === fdeEmail);
                if (!fde) {
                    toast.error('Selected engineer not found in roster');
                    return;
                }
                assignedFde = {
                    email: fde.email,
                    firstName: fde.firstName || '',
                    lastName: fde.lastName || '',
                    assignedAt: Date.now(),
                    assignedBy: 'dashboard',
                    assignmentMethod: 'manual',
                };
            }

            const orgRef = doc(activeFirestore, 'organizations', orgId);
            await updateDoc(orgRef, { assignedFde });

            const applyToUser = (u) => {
                const od = u.organizationData;
                if (!od) return u;
                let next = od;
                if (od.id === orgId) {
                    next = { ...next, assignedFde };
                }
                // Also mirror onto the matching invited membership so a swapped-in
                // (deactivated-primary) org reflects the change without a refetch.
                if (Array.isArray(od._allMemberships) && od._allMemberships.some((m) => m.orgId === orgId)) {
                    next = {
                        ...next,
                        _allMemberships: od._allMemberships.map((m) =>
                            m.orgId === orgId ? { ...m, orgAssignedFde: assignedFde } : m,
                        ),
                    };
                }
                return next === od ? u : { ...u, organizationData: next };
            };
            setAllUsers((prev) => prev.map(applyToUser));
            setEmailSearchResults((prev) => prev.map(applyToUser));

            toast.success(
                assignedFde
                    ? `Assigned ${assignedFde.firstName || assignedFde.email} as FDE`
                    : 'Cleared FDE assignment',
            );
        } catch (error) {
            console.error('Error assigning FDE:', error);
            toast.error('Failed to update forward-deployed engineer');
        } finally {
            setUpdatingFdeOrgId(null);
        }
    };

    const fetchOrganizationStats = async (organizationId) => {
        const stats = {
            loans: 0,
            leads: 0,
            documents: 0,
            borrowers: 0
        };

        try {
            const subcollections = ['loans', 'leads', 'documents', 'borrowers'];
            
            for (const subcollectionName of subcollections) {
                try {
                    const subcollectionRef = collection(activeFirestore, 'organizations', organizationId, subcollectionName);
                    const subcollectionSnapshot = await getDocs(subcollectionRef);
                    stats[subcollectionName] = subcollectionSnapshot.size;
                } catch (subcollectionError) {
                    console.warn(`Error fetching ${subcollectionName} count:`, subcollectionError);
                    stats[subcollectionName] = 0;
                }
            }
        } catch (error) {
            console.warn('Error fetching organization stats:', error);
        }

        return stats;
    };

    const fetchAllUsersBasic = async () => {
        if (!activeFirestore) {
            toast.error('Firebase is not initialized');
            return;
        }

        setIsLoadingAllUsers(true);
        setError(null);
        setAllUsers([]);
        setHasLoadedAllUsers(true);

        try {
            const usersRef = collection(activeFirestore, 'users');
            const q = query(usersRef, where('createdAccountOnClient', '==', true));
            const querySnapshot = await getDocs(q);

            if (querySnapshot.empty) {
                toast.info('No users found');
                setAllUsers([]);
                return;
            }

            const users = [];
            for (const docSnapshot of querySnapshot.docs) {
                const userData = docSnapshot.data();
                console.log(docSnapshot);
                
                users.push({
                    id: docSnapshot.id,
                    ...userData,
                    organizationData: null, // Will be loaded when accordion is opened
                    organizationLoaded: false
                });
            }

            // Sort users by email for easier browsing
            users.sort((a, b) => (a.email || '').localeCompare(b.email || ''));

            setAllUsers(users);
        } catch (error) {
            console.error('Error fetching filtered users:', error);
            setError('Failed to fetch filtered users');
            toast.error('Failed to fetch filtered users');
        } finally {
            setIsLoadingAllUsers(false);
        }
    };

    const fetchUserOrganizationData = async (userId, userOverride = null) => {
        if (!activeFirestore) {
            return null;
        }

        try {
            const user = userOverride ?? allUsers.find(u => u.id === userId);
            if (!user) return null;

            let organizationData = null;

            // Get organization data if user has an organization
            try {
                // First, try to find organization by defaultForUserId
                const orgQuery = query(collection(activeFirestore, 'organizations'), where('defaultForUserId', '==', userId));
                const orgSnapshot = await getDocs(orgQuery);
                
                if (!orgSnapshot.empty) {
                    organizationData = orgSnapshot.docs[0].data();
                    organizationData.id = orgSnapshot.docs[0].id;
                    
                    // Fetch subcollection counts
                    organizationData.stats = await fetchOrganizationStats(organizationData.id);
                } else if (user.organization) {
                    // If no organization found by defaultForUserId, try using the organization reference
                    const orgRef = doc(activeFirestore, 'organizations', user.organization);
                    const orgDocSnapshot = await getDoc(orgRef);
                    if (orgDocSnapshot.exists()) {
                        organizationData = orgDocSnapshot.data();
                        organizationData.id = orgDocSnapshot.id;
                        
                        // Fetch subcollection counts
                        organizationData.stats = await fetchOrganizationStats(organizationData.id);
                    }
                }

                if (!organizationData) {
                    // Fallback: try to find organization by uid field matching the user ID
                    const uidOrgQuery = query(collection(activeFirestore, 'organizations'), where('uid', '==', userId));
                    const uidOrgSnapshot = await getDocs(uidOrgQuery);
                    
                    if (!uidOrgSnapshot.empty) {
                        organizationData = uidOrgSnapshot.docs[0].data();
                        organizationData.id = uidOrgSnapshot.docs[0].id;
                        organizationData.stats = await fetchOrganizationStats(organizationData.id);
                    }
                }

                if (!organizationData && user.email) {
                    // Fallback: find org via organization-members collection (for team members who aren't the org owner)
                    const memberQuery = query(
                        collection(activeFirestore, 'organization-members'),
                        where('email', '==', user.email),
                        where('status', '==', 'accepted')
                    );
                    const memberSnapshot = await getDocs(memberQuery);

                    if (!memberSnapshot.empty) {
                        const membership = memberSnapshot.docs[0].data();
                        if (membership.organizationId) {
                            const orgRef = doc(activeFirestore, 'organizations', membership.organizationId);
                            const orgDocSnapshot = await getDoc(orgRef);
                            if (orgDocSnapshot.exists()) {
                                organizationData = orgDocSnapshot.data();
                                organizationData.id = orgDocSnapshot.id;
                                organizationData.isMember = true;
                                organizationData.memberRole = membership.role || 'member';
                                organizationData.stats = await fetchOrganizationStats(organizationData.id);
                            }
                        }
                    }
                }
            } catch (orgError) {
                console.warn('Error fetching organization for user:', userId, orgError);
            }

            if (organizationData?.defaultForUserId) {
                try {
                    const ownerSnap = await getDoc(
                        doc(activeFirestore, "users", organizationData.defaultForUserId)
                    );
                    if (ownerSnap.exists()) {
                        organizationData.ownerEmail = ownerSnap.data().email || null;
                    }
                } catch (ownerErr) {
                    console.warn("Error fetching owner email for organization:", ownerErr);
                }
            }

            // Fetch ALL organization memberships for this user (all statuses) to show invited orgs
            let allMemberships = [];
            if (user.email) {
                try {
                    const allMembershipsQuery = query(
                        collection(activeFirestore, 'organization-members'),
                        where('email', '==', user.email)
                    );
                    const allMembershipsSnapshot = await getDocs(allMembershipsQuery);

                    const membershipPromises = allMembershipsSnapshot.docs.map(async (memberDoc) => {
                        const memberData = memberDoc.data();
                        if (!memberData.organizationId) return null;
                        // Skip if this is already the primary org
                        if (organizationData && memberData.organizationId === organizationData.id) {
                            return null;
                        }
                        try {
                            const orgRef = doc(activeFirestore, 'organizations', memberData.organizationId);
                            const orgSnap = await getDoc(orgRef);
                            if (!orgSnap.exists()) return null;
                            const orgData = orgSnap.data();
                            return {
                                membershipId: memberDoc.id,
                                role: memberData.role || 'member',
                                status: memberData.status || 'unknown',
                                isAddy: memberData.isAddy || false,
                                createdAt: memberData.createdAt,
                                orgId: orgSnap.id,
                                orgName: orgData.name || 'Unnamed Organization',
                                orgActive: orgData.active !== false,
                                orgSubscription: orgData.subscription || null,
                                // FDE fields so an invited org swapped in for a deactivated
                                // primary can still show / manage its assigned engineer.
                                orgAssignedFde: orgData.assignedFde || null,
                                orgBilling: orgData.billing || null,
                                orgStripeCustomerId: orgData.stripeCustomerId || null,
                                orgWhiteLabelSettings: orgData.whiteLabelSettings || null,
                            };
                        } catch (err) {
                            console.warn('Error fetching org for membership:', memberData.organizationId, err);
                            return null;
                        }
                    });

                    allMemberships = (await Promise.all(membershipPromises)).filter(Boolean);
                    allMemberships.sort((a, b) => {
                        const dateA = a.createdAt?.toDate?.() || new Date(0);
                        const dateB = b.createdAt?.toDate?.() || new Date(0);
                        return dateB - dateA;
                    });
                } catch (membershipsErr) {
                    console.warn('Error fetching all memberships for user:', userId, membershipsErr);
                }
            }

            if (!organizationData && allMemberships.length === 0) return null;
            if (!organizationData) return { _allMemberships: allMemberships };
            return { ...organizationData, _allMemberships: allMemberships };
        } catch (error) {
            console.error('Error fetching user organization data:', error);
            return null;
        }
    };



    const toggleUserExpansion = async (userId) => {
        const isCurrentlyExpanded = expandedUsers.has(userId);
        
        // Toggle the expansion state
        setExpandedUsers(prev => {
            const newSet = new Set(prev);
            if (newSet.has(userId)) {
                newSet.delete(userId);
            } else {
                newSet.add(userId);
            }
            return newSet;
        });

        // If expanding and organization data hasn't been loaded yet, fetch it
        if (!isCurrentlyExpanded) {
            const user = allUsers.find(u => u.id === userId) || emailSearchResults.find(u => u.id === userId);
            if (user && !user.organizationLoaded) {
                // Set loading state for this user
                setLoadingUserDetails(prev => new Set(prev).add(userId));
                ensureReferralFormInitialized(user);
                
                try {
                    const organizationData = await fetchUserOrganizationData(userId, user);
                    const isFromMainList = allUsers.some(u => u.id === userId);
                    
                    if (isFromMainList) {
                        setAllUsers(prevUsers =>
                            prevUsers.map(u =>
                                u.id === userId
                                    ? {
                                        ...u,
                                        organizationData: organizationData,
                                        organizationLoaded: true
                                    }
                                    : u
                            )
                        );
                    } else {
                        setEmailSearchResults(prev =>
                            prev.map(u =>
                                u.id === userId
                                    ? {
                                        ...u,
                                        organizationData: organizationData,
                                        organizationLoaded: true
                                    }
                                    : u
                            )
                        );
                    }

                    // Also fetch organization members if organization exists
                    if (organizationData?.id) {
                        fetchOrganizationMembers(organizationData.id);
                    }
                } catch (error) {
                    console.error('Error loading organization data for user:', userId, error);
                    toast.error('Failed to load organization data');
                } finally {
                    // Remove loading state for this user
                    setLoadingUserDetails(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(userId);
                        return newSet;
                    });
                }
            }
        }
    };

    const updateEnterpriseGrowthBilling = async (user, orgRef, billing) => {
        await updateDoc(orgRef, { billing });

        setAllUsers(prevResults =>
            prevResults.map(result =>
                result.id === user.id
                    ? { ...result, organizationData: { ...result.organizationData, billing } }
                    : result
            )
        );
    };

    const removeFromEnterpriseGrowth = async (user) => {
        setIsUpdating(true);
        setUpdatingUserId(user.id);

        try {
            const orgRef = doc(activeFirestore, 'organizations', user.organizationData.id);
            const billing = {
                ...(user.organizationData.billing || {}),
                billingType: null,
                enterpriseGrowth: false,
                enterpriseGrowthRemovedAt: Date.now(),
                enterpriseGrowthRemovedBy: (useDevDb ? authDev : authProd)?.currentUser?.uid || 'admin-dashboard',
            };
            await updateEnterpriseGrowthBilling(user, orgRef, billing);
            toast.success('Organization moved back to Growth');
        } catch (error) {
            console.error('Error removing Enterprise Growth:', error);
            toast.error('Failed to move the organization back to Growth');
        } finally {
            setIsUpdating(false);
            setUpdatingUserId(null);
        }
    };

    const updateToEnterpriseSubscriptionAllUsers = async (user, billingUpdates) => {
        setIsUpdating(true);
        setUpdatingUserId(user.id);

        try {
            if (!user.organizationData || !user.organizationData.id) {
                toast.error('User does not have an organization');
                return;
            }

            const orgRef = doc(activeFirestore, 'organizations', user.organizationData.id);

            if (billingUpdates.enterpriseGrowth) {
                await updateEnterpriseGrowthBilling(user, orgRef, { ...(user.organizationData.billing || {}), ...billingUpdates });
                setActivationDialogUser(null);
                toast.success('Organization set to Enterprise Growth: keeps paying its Growth subscription');

                return;
            }

            // Prepare the enterprise subscription object
            const enterpriseSubscription = {
                priceId: "enterprise_monthly_price",
                provider: "manual",
                status: "active"
            };

            // Prepare organization updates
            const orgUpdates = {
                subscription: enterpriseSubscription,
                billing: { ...(user.organizationData.billing || {}), ...billingUpdates },
            };

            // If there's an existing subscription, preserve fields and move Stripe details to "old" fields
            if (user.organizationData.subscription) {
                const currentSub = user.organizationData.subscription;
                
                // Preserve timing and payment details
                enterpriseSubscription.current_period_end = currentSub.current_period_end || Date.now() + (30 * 24 * 60 * 60 * 1000); // 30 days from now if not set
                enterpriseSubscription.cycle = currentSub.cycle || "month";
                enterpriseSubscription.last_payment = currentSub.last_payment || Date.now();
                enterpriseSubscription.uid = currentSub.uid || user.id;
                
                // If this is a Stripe subscription being converted to Enterprise
                if (currentSub.provider === "stripe" && currentSub.priceId !== "enterprise_monthly_price") {
                    // Move Stripe subscription details to "old" fields
                    if (currentSub.id) {
                        enterpriseSubscription.oldId = currentSub.id;
                    }
                    if (currentSub.priceId) {
                        enterpriseSubscription.oldPriceId = currentSub.priceId;
                    }
                    
                    // Move stripeCustomerId to oldstripecustomerid
                    if (user.organizationData.stripeCustomerId) {
                        orgUpdates.oldstripecustomerid = user.organizationData.stripeCustomerId;
                        // Remove the current stripeCustomerId since it's now enterprise/manual
                        orgUpdates.stripeCustomerId = null;
                    }
                } else {
                    // If already has old details or not a stripe subscription, preserve existing old fields
                    if (currentSub.oldId) {
                        enterpriseSubscription.oldId = currentSub.oldId;
                    }
                    if (currentSub.oldPriceId) {
                        enterpriseSubscription.oldPriceId = currentSub.oldPriceId;
                    }
                }
            } else {
                enterpriseSubscription.uid = user.id;
            }

            // Update the organization with the new subscription and any additional fields
            await updateDoc(orgRef, orgUpdates);

            // Update the local state to reflect the change in all users list
            setAllUsers(prevResults =>
                prevResults.map(result =>
                    result.id === user.id
                        ? {
                            ...result,
                            organizationData: {
                                ...result.organizationData,
                                ...orgUpdates
                            }
                        }
                        : result
                )
            );

            setActivationDialogUser(null);
            toast.success(
                billingUpdates.billingExempt
                    ? 'Organization set to Enterprise (billing exempt)'
                    : `Organization set to Enterprise: $${billingUpdates.pricePerLoan} per funded loan from ${billingUpdates.billingStartDate}`
            );
        } catch (error) {
            console.error('Error updating subscription:', error);
            setError('Failed to update subscription');
            toast.error('Failed to update subscription to Enterprise');
        } finally {
            setIsUpdating(false);
            setUpdatingUserId(null);
        }
    };

    const removeFromEnterpriseSubscription = async (user, action) => {
        setIsUpdating(true);
        setUpdatingUserId(user.id);

        try {
            if (!user.organizationData || !user.organizationData.id) {
                toast.error('User does not have an organization');
                return;
            }

            const orgRef = doc(activeFirestore, 'organizations', user.organizationData.id);
            const currentSub = user.organizationData.subscription;
            
            if (!currentSub || currentSub.priceId !== 'enterprise_monthly_price') {
                toast.error('User is not currently on Enterprise subscription');
                return;
            }

            let orgUpdates = {};

            if (action === 'rollback' && (currentSub.oldId || currentSub.oldPriceId)) {
                // Rollback to old subscription
                const rollbackSubscription = {
                    id: currentSub.oldId || null,
                    priceId: currentSub.oldPriceId || null,
                    provider: "stripe",
                    status: "active",
                    uid: currentSub.uid || user.id,
                    current_period_end: currentSub.current_period_end || Date.now() + (30 * 24 * 60 * 60 * 1000),
                    cycle: currentSub.cycle || "month",
                    last_payment: currentSub.last_payment || Date.now()
                };

                orgUpdates = {
                    subscription: rollbackSubscription,
                    stripeCustomerId: user.organizationData.oldstripecustomerid || null
                };

                // Clean up old fields
                if (user.organizationData.oldstripecustomerid) {
                    orgUpdates.oldstripecustomerid = null;
                }

                toast.success('Successfully rolled back to previous subscription');
            } else if (action === 'remove') {
                // Remove subscription completely
                orgUpdates = {
                    subscription: null
                };

                toast.success('Successfully removed subscription completely');
            } else {
                toast.error('No previous subscription found to rollback to');
                return;
            }

            // Update the organization
            await updateDoc(orgRef, orgUpdates);

            // Update the local state to reflect the change
            setAllUsers(prevResults =>
                prevResults.map(result =>
                    result.id === user.id
                        ? {
                            ...result,
                            organizationData: {
                                ...result.organizationData,
                                ...orgUpdates
                            }
                        }
                        : result
                )
            );

        } catch (error) {
            console.error('Error removing from Enterprise subscription:', error);
            setError('Failed to remove from Enterprise subscription');
            toast.error('Failed to remove from Enterprise subscription');
        } finally {
            setIsUpdating(false);
            setUpdatingUserId(null);
        }
    };

    const getSubscriptionStatus = (organizationData) => {
        if (!organizationData || !organizationData.subscription) {
            return { status: 'No Subscription', badgeClass: 'bg-secondary', priceId: 'None', isDeactivated: organizationData?.active === false };
        }

        const sub = organizationData.subscription;
        const isEnterprise = sub.priceId === 'enterprise_monthly_price';
        const isDeactivated = organizationData.active === false;
        const rawStatus = typeof sub.status === 'string' ? sub.status.toLowerCase() : '';
        const isCancelingAtPeriodEnd = Boolean(sub.cancelAtPeriodEnd || sub.cancel_at);

        // Canceled subs take priority over the plan label so admins see the true state.
        if (rawStatus === 'canceled') {
            return {
                status: 'Canceled',
                badgeClass: 'bg-danger',
                priceId: sub.priceId || 'Unknown',
                isDeactivated,
            };
        }

        // Still active but scheduled to cancel at the end of the billing period.
        if (isCancelingAtPeriodEnd && (rawStatus === 'active' || rawStatus === 'trialing' || rawStatus === '' )) {
            return {
                status: isEnterprise ? 'Enterprise — Cancels at period end' : 'Cancels at period end',
                badgeClass: 'bg-warning',
                priceId: sub.priceId || 'Unknown',
                isDeactivated,
            };
        }

        // Surface other non-active Stripe states explicitly instead of masking them as active.
        if (rawStatus && rawStatus !== 'active') {
            const label = rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1);
            return {
                status: isEnterprise ? `Enterprise — ${label}` : label,
                badgeClass: 'bg-warning',
                priceId: sub.priceId || 'Unknown',
                isDeactivated,
            };
        }

        if (isEnterpriseGrowthOrg(organizationData)) {
            return {
                status: 'Enterprise Growth',
                badgeClass: 'bg-success',
                priceId: sub.priceId || 'Unknown',
                isDeactivated,
            };
        }

        return {
            status: isEnterprise ? 'Enterprise' : 'Client Stripe Subscription',
            badgeClass: isEnterprise ? 'bg-success' : 'bg-warning',
            priceId: sub.priceId || 'Unknown',
            isDeactivated,
        };
    };

    // Whether an org qualifies for a forward-deployed engineer. Mirrors backend
    // services/fdeAssignment.js deriveFdePlanTier: real paying signals only.
    //   Growth = a live Stripe subscription (excluding Essentials).
    //   Enterprise = an enterprise-price org carrying a real-customer signal
    //     (billing config, stripeCustomerId, a connected LOS integration, or
    //     whiteLabelSettings). The mass-granted manual enterprise_monthly_price
    //     freebie with no signal is intentionally NOT eligible.
    const FDE_ENTERPRISE_PRICE_IDS = ['enterprise_monthly_price', 'enterprise_yearly_price'];
    const FDE_INTEGRATION_KEYS = [
        'encompassEPC', 'encompass', 'meridianLink', 'meridianlink', 'byteEPC',
        'losSystem', 'los', 'losType', 'integrations', 'connectedIntegrations',
    ];
    const hasEnterpriseCustomerSignal = (organizationData) => {
        const billing = organizationData.billing || {};
        if (billing.billingExempt === true) return true;
        if (billing.billingType) return true;
        if (typeof organizationData.stripeCustomerId === 'string' && organizationData.stripeCustomerId.trim().length > 0) return true;
        if (organizationData.whiteLabelSettings && typeof organizationData.whiteLabelSettings === 'object') return true;
        return FDE_INTEGRATION_KEYS.some((key) => {
            const value = organizationData[key];
            if (value === undefined || value === null || value === '') return false;
            if (typeof value === 'object') return Object.keys(value).length > 0;
            return true;
        });
    };
    const isFdeEligibleOrg = (organizationData) => {
        if (!organizationData) return false;
        const sub = organizationData.subscription || {};
        const isStripeLive = sub.provider === 'stripe' && ['active', 'trialing'].includes(sub.status);
        if (isStripeLive) {
            const priceId = (sub.priceId || '').toLowerCase();
            return !priceId.includes('essentials');
        }
        const isEnterprisePrice = FDE_ENTERPRISE_PRICE_IDS.includes(sub.priceId);
        return isEnterprisePrice && hasEnterpriseCustomerSignal(organizationData);
    };

    // Fetch organization members from the root organization-members collection
    const fetchOrganizationMembers = async (organizationId) => {
        if (!activeFirestore || !organizationId) return [];

        try {
            setLoadingMembers(prev => new Set(prev).add(organizationId));
            
            // Query the root organization-members collection by organizationId
            const membersRef = collection(activeFirestore, 'organization-members');
            const q = query(membersRef, where('organizationId', '==', organizationId));
            const membersSnapshot = await getDocs(q);
            
            const members = membersSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            // Sort by createdAt (newest first)
            members.sort((a, b) => {
                const dateA = a.createdAt?.toDate?.() || new Date(0);
                const dateB = b.createdAt?.toDate?.() || new Date(0);
                return dateB - dateA;
            });

            setOrganizationMembers(prev => ({
                ...prev,
                [organizationId]: members
            }));

            return members;
        } catch (error) {
            console.error('Error fetching organization members:', error);
            toast.error('Failed to fetch organization members');
            return [];
        } finally {
            setLoadingMembers(prev => {
                const newSet = new Set(prev);
                newSet.delete(organizationId);
                return newSet;
            });
        }
    };

    // Open invite dialog
    const openInviteDialog = (organizationId, organizationName) => {
        setInviteDialogOrgId(organizationId);
        setInviteDialogOrgName(organizationName || 'Organization');
        setInviteDialogOpen(true);
    };

    // Close invite dialog
    const closeInviteDialog = () => {
        setInviteDialogOpen(false);
        setInviteDialogOrgId(null);
        setInviteDialogOrgName('');
    };

    // Handle successful invitation
    const handleInviteSuccess = () => {
        if (inviteDialogOrgId) {
            fetchOrganizationMembers(inviteDialogOrgId);
        }
    };

    // Open Enterprise Billing Dialog
    const openBillingDialog = (user) => {
        setBillingDialogUser(user);
        setBillingDialogOpen(true);
    };

    const closeBillingDialog = () => {
        setBillingDialogOpen(false);
        setBillingDialogUser(null);
    };

    // When billing dialog updates org data, reflect it in local state
    const handleBillingOrgUpdate = (updatedFields) => {
        if (!billingDialogUser) return;
        const userId = billingDialogUser.id;

        const updateUserOrgData = (users) =>
            users.map(u =>
                u.id === userId
                    ? { ...u, organizationData: { ...u.organizationData, ...updatedFields } }
                    : u
            );

        setAllUsers(updateUserOrgData);
        setEmailSearchResults(updateUserOrgData);

        setBillingDialogUser(prev => prev ? {
            ...prev,
            organizationData: { ...prev.organizationData, ...updatedFields },
        } : prev);
    };

    // Billing saved from inside the Set to Enterprise dialog (stage rules, per-user prices)
    const handleActivationOrgUpdate = (updatedFields) => {
        if (!activationDialogUser) return;
        const userId = activationDialogUser.id;

        const updateUserOrgData = (users) =>
            users.map(u =>
                u.id === userId
                    ? { ...u, organizationData: { ...u.organizationData, ...updatedFields } }
                    : u
            );

        setAllUsers(updateUserOrgData);
        setEmailSearchResults(updateUserOrgData);

        setActivationDialogUser(prev => prev ? {
            ...prev,
            organizationData: { ...prev.organizationData, ...updatedFields },
        } : prev);
    };

    // Get status badge class for members
    const getMemberStatusBadge = (status) => {
        switch (status?.toLowerCase()) {
            case 'accepted':
                return 'bg-success';
            case 'pending':
                return 'bg-warning';
            case 'rejected':
                return 'bg-danger';
            default:
                return 'bg-secondary';
        }
    };

    // Derive the current select value for a user: prefer the in-progress edit, then fall back to stored value.
    // Returns a PARTNER_OPTIONS slug or CUSTOM_PARTNER_SENTINEL.
    const getPartnerSlugForUser = (user) => {
        if (!user?.id) return '';
        if (user.id in partnerSlugByUserId) return partnerSlugByUserId[user.id];
        // Initialise from stored promotekitReferralCode; normalise to known slugs.
        const stored = (user?.promotekitReferralCode || '').toLowerCase().trim();
        const match = PARTNER_OPTIONS.find((o) => o.slug && o.slug !== CUSTOM_PARTNER_SENTINEL && stored === o.slug);
        if (match) return match.slug;
        // If there's a stored value that doesn't match any known slug, treat it as custom.
        if (stored) return CUSTOM_PARTNER_SENTINEL;
        return '';
    };

    // Resolve the actual slug to persist: if custom sentinel is selected, use the free-text input (lowercased).
    const resolvePartnerSlugToSave = (user) => {
        const selectValue = getPartnerSlugForUser(user);
        if (selectValue === CUSTOM_PARTNER_SENTINEL) {
            return (customPartnerSlugByUserId[user.id] || '').toLowerCase().trim();
        }
        return selectValue;
    };

    const ensureReferralFormInitialized = (user) => {
        if (!user?.id || user.id in partnerSlugByUserId) return;
        const selectValue = getPartnerSlugForUser(user);
        setPartnerSlugByUserId((prev) => ({ ...prev, [user.id]: selectValue }));
        // If the stored value is custom, pre-populate the free-text input.
        if (selectValue === CUSTOM_PARTNER_SENTINEL) {
            const stored = (user?.promotekitReferralCode || '').toLowerCase().trim();
            setCustomPartnerSlugByUserId((prev) => ({ ...prev, [user.id]: stored }));
        }
    };

    const saveReferralDetails = async (user) => {
        if (!activeFirestore || !user?.id) return;
        const slug = resolvePartnerSlugToSave(user);
        const updates = {
            promotekitReferralCode: slug || null,
        };

        setSavingReferralUserId(user.id);
        try {
            const userRef = doc(activeFirestore, 'users', user.id);
            await updateDoc(userRef, updates);

            setAllUsers((prevUsers) =>
                prevUsers.map((existingUser) =>
                    existingUser.id === user.id
                        ? { ...existingUser, ...updates }
                        : existingUser
                )
            );
            setEmailSearchResults((prevUsers) =>
                prevUsers.map((existingUser) =>
                    existingUser.id === user.id
                        ? { ...existingUser, ...updates }
                        : existingUser
                )
            );

            toast.success('Partner referral updated');
        } catch (err) {
            console.error('Failed to update partner referral:', err);
            toast.error('Failed to update partner referral');
        } finally {
            setSavingReferralUserId(null);
        }
    };



    return (
        <div className="container-fluid p-4 UserPermissionsPageContainer" >
            <style dangerouslySetInnerHTML={{ __html: accordionStyles }} />
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
            
            {/* Header with Database Toggle */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h1 className="mb-0">User Permissions</h1>
                
                {/* Database Environment Toggle */}
                <div className="d-flex align-items-center gap-3">
                    <div className="d-flex align-items-center">
                        <FaDatabase className="me-2 text-muted" />
                        <span className={`me-2 ${!useDevDb ? 'fw-bold text-success' : 'text-muted'}`}>
                            Prod
                        </span>
                        <div className="form-check form-switch mb-0">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                id="dbEnvironmentSwitch"
                                checked={useDevDb}
                                onChange={(e) => {
                                    setUseDevDb(e.target.checked);
                                    toast.info(`Switched to ${e.target.checked ? 'Development' : 'Production'} database`);
                                }}
                                style={{ cursor: 'pointer', width: '3rem', height: '1.5rem' }}
                            />
                        </div>
                        <span className={`ms-2 ${useDevDb ? 'fw-bold text-warning' : 'text-muted'}`}>
                            Dev
                        </span>
                    </div>
                    <span className={`badge ${useDevDb ? 'bg-warning' : 'bg-success'}`}>
                        {useDevDb ? 'DEV Database' : 'PROD Database'}
                    </span>
                </div>
            </div>

            {/* Instructions Section - Collapsible */}
            <div className="card mb-4">
                <div className="card-header">
                    <h2 className="h5 mb-0">
                        <button 
                            className="accordion-button collapsed p-2 text-decoration-none w-100 d-flex justify-content-between align-items-center border-0"
                            type="button" 
                            data-bs-toggle="collapse" 
                            data-bs-target="#instructionsCollapse" 
                            aria-expanded="false" 
                            aria-controls="instructionsCollapse"
                        >
                            <span>📖 How to Manage Users</span>
                            <span className="accordion-arrow">▼</span>
                        </button>
                    </h2>
                </div>
                <div className="collapse" id="instructionsCollapse">
                    <div className="card-body">
                        <div className="row">
                            <div className="col-md-6">
                                <h6>✅ How to Use:</h6>
                                <ol>
                                    <li>All users are automatically loaded when you open this page</li>
                                    <li>Use the search box to find specific users</li>
                                    <li>Click on any user row to see more details</li>
                                    <li>Click "Set to Enterprise" to upgrade their subscription</li>
                                    <li>For Enterprise users: Use "Rollback to Previous" to restore their old subscription</li>
                                    <li>For Enterprise users: Use "Remove Subscription" to remove their subscription completely</li>
                                    <li><strong>NEW:</strong> Click "Invite Member" to add new members to an organization</li>
                                </ol>
                                
                                <h6 className="mt-4">🔍 Search Options:</h6>
                                <ul>
                                    <li><strong>All fields</strong> — substring match on loaded users; extra Firestore lookups for full email, long user/org IDs, and exact org name when you pick a specific mode</li>
                                    <li><strong>Email</strong> — filter by email; exact address also queries <code>users</code> (finds accounts without <code>createdAccountOnClient</code>)</li>
                                    <li><strong>User ID</strong> — filter by user id; full id also loads that user document directly</li>
                                    <li><strong>Organization ID</strong> — filter by org id on loaded rows; full id loads the org and resolves the owner user</li>
                                    <li><strong>Organization name</strong> — substring on loaded org names; exact name also queries <code>organizations</code> and loads owners</li>
                                </ul>

                                <h6 className="mt-4">👥 Member Invitations:</h6>
                                <ul>
                                    <li>Click on a user to expand their details</li>
                                    <li>View existing organization members</li>
                                    <li>Click "Invite Member" to add a new member</li>
                                    <li>Set the member's email, role, and Addy team status</li>
                                </ul>
                            </div>
                            <div className="col-md-6">
                                <h6>📊 Subscription Status:</h6>
                                <ul className="list-unstyled">
                                    <li className="mb-2">
                                        <span className="badge bg-success me-2">Enterprise</span>
                                        User has enterprise subscription
                                        <br /><small className="text-muted">Shows rollback if previous subscription exists, otherwise shows remove option</small>
                                    </li>
                                    <li className="mb-2">
                                        <span className="badge bg-warning me-2">Client Stripe Subscription</span>
                                        User has a different subscription plan
                                        <br /><small className="text-muted">Can upgrade to Enterprise</small>
                                    </li>
                                    <li className="mb-2">
                                        <span className="badge bg-secondary me-2">No Subscription</span>
                                        User doesn't have an active subscription
                                        <br /><small className="text-muted">Can upgrade to Enterprise</small>
                                    </li>
                                </ul>

                                <h6 className="mt-4">👤 Member Status:</h6>
                                <ul className="list-unstyled">
                                    <li className="mb-1">
                                        <span className="badge bg-success me-2">accepted</span>
                                        Member has accepted the invitation
                                    </li>
                                    <li className="mb-1">
                                        <span className="badge bg-warning me-2">pending</span>
                                        Invitation sent, waiting for acceptance
                                    </li>
                                    <li className="mb-1">
                                        <span className="badge bg-info me-2">Addy Team</span>
                                        Member is part of the Addy AI team
                                    </li>
                                </ul>
                            </div>
                        </div>
                        
                        <div className="alert alert-info mt-3" role="alert">
                            <strong>💡 Tip:</strong> Click on any user row to expand and see their organization details, statistics, and subscription management options.
                        </div>
                    </div>
                </div>
            </div>

            {/* Client Users Management with Real-time Search */}
            <div className="card mb-4">
                <div className="card-header">
                    <h2 className="h5 mb-0">Client Users Management</h2>
                    <small className="text-muted">Showing users</small>
                    {isLoadingAllUsers && (
                        <div className="mt-2">
                            <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                            <span className="text-muted">Loading client users...</span>
                        </div>
                    )}
                    {allUsers.length > 0 && (
                        <div className="mt-2">
                            <span className="text-muted">
                                Total client users: {allUsers.length}
                                {(tableSearchTerm.trim() || authProviderFilter !== "all") && (
                                    <span> | Filtered: {displayedUsers.length}</span>
                                )}
                            </span>
                        </div>
                    )}
                </div>
                <div className="card-body">

                    {/* Real-time search input for table */}
                    {allUsers.length > 0 && (
                        <div className="mb-3">
                            <div className="row g-2 align-items-end">
                                <div className="col-md-3 col-lg-2">
                                    <label htmlFor="userSearchMode" className="form-label">Search by</label>
                                    <select
                                        id="userSearchMode"
                                        className="form-select"
                                        value={userSearchMode}
                                        onChange={(e) => setUserSearchMode(e.target.value)}
                                    >
                                        {USER_SEARCH_MODES.map((m) => (
                                            <option key={m.value} value={m.value}>
                                                {m.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-3 col-lg-2">
                                    <label htmlFor="authProviderFilter" className="form-label">Last sign-in method</label>
                                    <select
                                        id="authProviderFilter"
                                        className="form-select"
                                        value={authProviderFilter}
                                        onChange={(e) => setAuthProviderFilter(e.target.value)}
                                    >
                                        {AUTH_PROVIDER_FILTER_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-6 col-lg-8">
                                    <label htmlFor="tableSearch" className="form-label">Filter users</label>
                                    <div className="input-group">
                                        <span className="input-group-text">
                                            <FaSearch />
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="tableSearch"
                                            placeholder={searchModeMeta.placeholder}
                                            value={tableSearchTerm}
                                            onChange={(e) => setTableSearchTerm(e.target.value)}
                                        />
                                        {tableSearchTerm && (
                                            <button
                                                className="btn btn-outline-secondary"
                                                type="button"
                                                onClick={() => setTableSearchTerm('')}
                                                title="Clear filter"
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>
                                    {(tableSearchTerm || authProviderFilter !== "all") && (
                                        <small className="text-muted">
                                            Showing {displayedUsers.length} of {allUsers.length} users
                                            {emailSearchResults.length > 0 && (
                                                <span className="text-muted">
                                                    {' '}
                                                    (+{emailSearchResults.length} from Firestore lookup)
                                                </span>
                                            )}
                                            {isSearchingByEmail && (
                                                <span className="ms-2">
                                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                    <span className="ms-1">Searching…</span>
                                                </span>
                                            )}
                                        </small>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="alert alert-danger" role="alert">
                            {error}
                        </div>
                    )}

                    {/* Users Table with Accordion */}
                    {displayedUsers.length > 0 && (
                        <div>
                            <div className="table-responsive">
                                <table className="table table-hover">
                                    <thead className="table-light">
                                        <tr>
                                            <th width="50"></th>
                                            <th>Email</th>
                                            <th>Last Sign-in</th>
                                            <th>Organization</th>
                                            <th>Subscription</th>
                                            <th>Sign Up Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {displayedUsers.map((user) => {
                                            const { displayOrg, deactivatedOrg } = getDisplayOrg(user);
                                            const subStatus = getSubscriptionStatus(displayOrg);
                                            const isExpanded = expandedUsers.has(user.id);
                                            const isLoadingDetails = loadingUserDetails.has(user.id);
                                            const isEnterpriseGrowth = isEnterpriseGrowthOrg(displayOrg);
                                            const isAlreadyEnterprise = subStatus.priceId === 'enterprise_monthly_price' || isEnterpriseGrowth;
                                            const currentPartnerSlug = getPartnerSlugForUser(user);
                                            const isShowingDeactivatedOrg = showDeactivatedOrgFor.has(user.id);
                                            
                                            return (
                                                <React.Fragment key={user.id}>
                                                    {/* Main Row - Clickable */}
                                                    <tr 
                                                        className="cursor-pointer"
                                                        onClick={() => toggleUserExpansion(user.id)}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <td>
                                                            {isLoadingDetails ? (
                                                                <div className="spinner-border spinner-border-sm text-muted" role="status"></div>
                                                            ) : isExpanded ? (
                                                                <FaChevronDown className="text-muted" />
                                                            ) : (
                                                                <FaChevronRight className="text-muted" />
                                                            )}
                                                        </td>
                                                        <td>
                                                            <strong>{user.email}</strong>
                                                            <br />
                                                            <small className="text-muted">{user.id}</small>
                                                        </td>
                                                        <td>
                                                            <AuthProviderBadge provider={user.lastAuthProvider} />
                                                        </td>
                                                        <td>
                                                            {user.organizationLoaded ? (
                                                                displayOrg?.name ? (
                                                                    <span>
                                                                        {displayOrg.name}
                                                                        {deactivatedOrg && (
                                                                            <button
                                                                                className="btn btn-link btn-sm p-0 ms-2 text-danger"
                                                                                style={{ fontSize: '0.75rem', verticalAlign: 'baseline' }}
                                                                                onClick={(e) => toggleDeactivatedOrg(user.id, e)}
                                                                                title="Primary org is deactivated — click to view it"
                                                                            >
                                                                                {isShowingDeactivatedOrg ? 'Hide deactivated org' : 'See deactivated org'}
                                                                            </button>
                                                                        )}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-muted">No organization</span>
                                                                )
                                                            ) : (
                                                                <span className="text-muted">Click to load...</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {user.organizationLoaded ? (
                                                                <span className={`badge ${subStatus.badgeClass}`}>
                                                                    {subStatus.status}
                                                                </span>
                                                            ) : (
                                                                <span className="badge bg-secondary">Unknown</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {formatSignedUpDate(user) || (
                                                                <span className="text-muted">Unknown</span>
                                                            )}
                                                        </td>
                                                    </tr>

                                                    {/* Expanded Row - Details */}
                                                    {isExpanded && (
                                                        <tr>
                                                            <td colSpan="6" className="bg-light">
                                                                {isLoadingDetails ? (
                                                                    <div className="p-3 text-center">
                                                                        <div className="spinner-border me-2" role="status"></div>
                                                                        <span>Loading organization details...</span>
                                                                    </div>
                                                                ) : (
                                                                    <div className="p-3">
                                                                        <div className="row">
                                                                            {/* User Details */}
                                                                            <div className="col-md-4">
                                                                                <h6><FaUser className="me-2" />User Details</h6>
                                                                                <ul className="list-unstyled">
                                                                                    <li><strong>Email:</strong> {user.email}</li>
                                                                                    <li><strong>User ID:</strong> {user.id}</li>
                                                                                    <li>
                                                                                        <strong>Signed Up:</strong>{" "}
                                                                                        {formatSignedUpDate(user) || (
                                                                                            <span className="text-muted">Unknown</span>
                                                                                        )}
                                                                                    </li>
                                                                                    {user.name && (
                                                                                        <li><strong>Name:</strong> {user.name}</li>
                                                                                    )}
                                                                                    <li className="d-flex align-items-center gap-2">
                                                                                        <strong>Last sign-in method:</strong>
                                                                                        <AuthProviderBadge provider={user.lastAuthProvider} />
                                                                                    </li>
                                                                                    <li><strong>Partner Referral:</strong> {
                                                                                        (() => {
                                                                                            const stored = (user?.promotekitReferralCode || '').toLowerCase().trim();
                                                                                            const match = PARTNER_OPTIONS.find((o) => o.slug && o.slug !== CUSTOM_PARTNER_SENTINEL && stored === o.slug);
                                                                                            return match ? match.label : (stored ? stored : 'Not set');
                                                                                        })()
                                                                                    }</li>
                                                                                </ul>
                                                                            </div>

                                                                            {/* Organization Details */}
                                                                            <div className="col-md-4">
                                                                                <h6>
                                                                                    <FaBuilding className="me-2" />
                                                                                    Organization Details
                                                                                    {deactivatedOrg && (
                                                                                        <span className="badge bg-warning text-dark ms-2" style={{ fontSize: '0.7rem' }}>Active invited org shown</span>
                                                                                    )}
                                                                                </h6>
                                                                {displayOrg?.name || displayOrg?.id ? (
                                                                    <div>
                                                                        <ul className="list-unstyled">
                                                                            <li>
                                                                                <strong>Name:</strong>{' '}
                                                                                {displayOrg.name || <span className="text-muted">Unnamed</span>}
                                                                                {displayOrg.active === false && (
                                                                                    <span className="badge bg-danger ms-2">Deactivated</span>
                                                                                )}
                                                                                {displayOrg._isSwappedFromDeactivated && (
                                                                                    <span className="badge bg-info ms-2" title="User's primary org is deactivated; showing first active invited org">Invited</span>
                                                                                )}
                                                                            </li>
                                                                            <li><strong>Org ID:</strong> {displayOrg.id}</li>
                                                                                            {displayOrg.defaultForUserId && (
                                                                                                <li>
                                                                                                    <strong>Owner (default user):</strong>{' '}
                                                                                                    {displayOrg.ownerEmail ? (
                                                                                                        <>
                                                                                                            {displayOrg.ownerEmail}
                                                                                                            <small className="text-muted ms-1">
                                                                                                                ({displayOrg.defaultForUserId})
                                                                                                            </small>
                                                                                                        </>
                                                                                                    ) : (
                                                                                                        <span className="text-muted">
                                                                                                            {displayOrg.defaultForUserId}
                                                                                                        </span>
                                                                                                    )}
                                                                                                </li>
                                                                                            )}
                                                                                            {displayOrg.stripeCustomerId && (
                                                                                                <li><strong>Stripe ID:</strong> {displayOrg.stripeCustomerId}</li>
                                                                                            )}
                                                                                        </ul>
                                                                                        
                                                                                        {displayOrg.stats && (
                                                                                            <div className="mt-2">
                                                                                                <strong>Statistics:</strong>
                                                                                                <div className="row g-2 mt-1">
                                                                                                    <div className="col-6">
                                                                                                        <span className="badge bg-primary me-1">{displayOrg.stats.loans}</span>
                                                                                                        <small>Loans</small>
                                                                                                    </div>
                                                                                                    <div className="col-6">
                                                                                                        <span className="badge bg-success me-1">{displayOrg.stats.leads}</span>
                                                                                                        <small>Leads</small>
                                                                                                    </div>
                                                                                                    <div className="col-6">
                                                                                                        <span className="badge bg-info me-1">{displayOrg.stats.documents}</span>
                                                                                                        <small>Documents</small>
                                                                                                    </div>
                                                                                                    <div className="col-6">
                                                                                                        <span className="badge bg-warning me-1">{displayOrg.stats.borrowers}</span>
                                                                                                        <small>Borrowers</small>
                                                                                                    </div>
                                                                                                </div>
                                                                                            </div>
                                                                                        )}

                                                                                        {/* Connected LOS integrations (read directly from org doc) */}
                                                                                        <div className="mt-2">
                                                                                            <strong>Integrations:</strong>
                                                                                            <div className="mt-1 d-flex flex-wrap gap-1">
                                                                                                {(() => {
                                                                                                    const integrations = getConnectedIntegrations(displayOrg);
                                                                                                    if (integrations.length === 0) {
                                                                                                        return <span className="text-muted">No integrations connected</span>;
                                                                                                    }
                                                                                                    return integrations.map((integration) => (
                                                                                                        <span
                                                                                                            key={integration.key}
                                                                                                            className={`badge ${INTEGRATION_STATUS_BADGE[integration.status] || 'bg-secondary'}`}
                                                                                                            title={INTEGRATION_STATUS_LABEL[integration.status] || integration.status}
                                                                                                        >
                                                                                                            {integration.label}
                                                                                                            {integration.status !== 'approved' && (
                                                                                                                <span className="ms-1" style={{ fontWeight: 400, opacity: 0.85 }}>
                                                                                                                    · {INTEGRATION_STATUS_LABEL[integration.status] || integration.status}
                                                                                                                </span>
                                                                                                            )}
                                                                                                        </span>
                                                                                                    ));
                                                                                                })()}
                                                                                            </div>
                                                                                        </div>

                                                                                        {/* Forward Deployed AI Engineer (Growth + Enterprise orgs) */}
                                                                                        {(isFdeEligibleOrg(displayOrg) || displayOrg.assignedFde) && (
                                                                                            <div className="mt-3">
                                                                                                <strong>Forward Deployed Engineer:</strong>
                                                                                                {displayOrg.assignedFde?.email ? (
                                                                                                    <div className="small text-muted mt-1">
                                                                                                        {(`${displayOrg.assignedFde.firstName || ''} ${displayOrg.assignedFde.lastName || ''}`).trim() || displayOrg.assignedFde.email}
                                                                                                        {' '}({displayOrg.assignedFde.email})
                                                                                                        {displayOrg.assignedFde.assignmentMethod && (
                                                                                                            <span className="badge bg-light text-dark ms-2" style={{ fontSize: '0.65rem' }}>
                                                                                                                {displayOrg.assignedFde.assignmentMethod}
                                                                                                            </span>
                                                                                                        )}
                                                                                                    </div>
                                                                                                ) : (
                                                                                                    <div className="small text-warning mt-1">Not yet assigned</div>
                                                                                                )}
                                                                                                <select
                                                                                                    className="form-select form-select-sm mt-2"
                                                                                                    value={displayOrg.assignedFde?.email || ''}
                                                                                                    disabled={updatingFdeOrgId === displayOrg.id || fdeRoster.length === 0}
                                                                                                    onClick={(e) => e.stopPropagation()}
                                                                                                    onChange={(e) => handleAssignFde(user, displayOrg.id, e.target.value)}
                                                                                                >
                                                                                                    <option value="">
                                                                                                        {fdeRoster.length === 0 ? 'No engineers in roster' : '— Unassigned —'}
                                                                                                    </option>
                                                                                                    {fdeRoster.map((fde) => (
                                                                                                        <option key={fde.id} value={fde.email}>
                                                                                                            {fde.fullName} ({fde.email})
                                                                                                        </option>
                                                                                                    ))}
                                                                                                </select>
                                                                                                {updatingFdeOrgId === displayOrg.id && (
                                                                                                    <small className="text-muted">Saving…</small>
                                                                                                )}
                                                                                            </div>
                                                                                        )}

                                                                                        {/* Deactivated org toggle button */}
                                                                                        {deactivatedOrg && (
                                                                                            <div className="mt-3">
                                                                                                <button
                                                                                                    className="btn btn-sm btn-outline-danger"
                                                                                                    onClick={(e) => toggleDeactivatedOrg(user.id, e)}
                                                                                                >
                                                                                                    {isShowingDeactivatedOrg ? '▲ Hide deactivated org' : '▼ See deactivated org'}
                                                                                                </button>
                                                                                            </div>
                                                                                        )}

                                                                                        {/* Deactivated org details panel */}
                                                                                        {deactivatedOrg && isShowingDeactivatedOrg && (
                                                                                            <div className="mt-3 p-2 border border-danger rounded" style={{ background: '#fff5f5' }}>
                                                                                                <div className="d-flex align-items-center mb-2">
                                                                                                    <span className="badge bg-danger me-2">Deactivated Org</span>
                                                                                                    <small className="text-muted">Primary / owned organization</small>
                                                                                                </div>
                                                                                                <ul className="list-unstyled mb-0 small">
                                                                                                    <li><strong>Name:</strong> {deactivatedOrg.name || <span className="text-muted">Unnamed</span>}</li>
                                                                                                    <li><strong>Org ID:</strong> {deactivatedOrg.id}</li>
                                                                                                    {deactivatedOrg.defaultForUserId && (
                                                                                                        <li>
                                                                                                            <strong>Owner:</strong>{' '}
                                                                                                            {deactivatedOrg.ownerEmail || deactivatedOrg.defaultForUserId}
                                                                                                        </li>
                                                                                                    )}
                                                                                                    {deactivatedOrg.stripeCustomerId && (
                                                                                                        <li><strong>Stripe ID:</strong> {deactivatedOrg.stripeCustomerId}</li>
                                                                                                    )}
                                                                                                    {deactivatedOrg.subscription?.priceId && (
                                                                                                        <li><strong>Subscription:</strong> {deactivatedOrg.subscription.priceId}</li>
                                                                                                    )}
                                                                                                </ul>
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                ) : (
                                                                                    <span className="text-muted">No organization associated</span>
                                                                                )}
                                                                            </div>

                                                                            {/* Subscription & Actions */}
                                                                            <div className="col-md-4">
                                                                                <h6>Subscription & Actions</h6>
                                                                                <div className="mb-3">
                                                                                    <span className={`badge ${subStatus.badgeClass} me-2`}>
                                                                                        {subStatus.status}
                                                                                    </span>
                                                                                    {subStatus.priceId !== 'None' && (
                                                                                        <div className="mt-1">
                                                                                            <small className="text-muted">
                                                                                                Price ID: {subStatus.priceId}
                                                                                            </small>
                                                                                        </div>
                                                                                    )}
                                                                                </div>

                                                                                {displayOrg?.id ? (
                                                                                    <div className="d-flex flex-column gap-2">
                                                                                        {!isAlreadyEnterprise ? (
                                                                                            <button
                                                                                                className="btn btn-sm btn-success"
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    setActivationDialogUser(user);
                                                                                                }}
                                                                                                disabled={isUpdating}
                                                            >
                                                                                                {updatingUserId === user.id ? (
                                                                                                    'Updating...'
                                                                                                ) : (
                                                                                                    'Set to Enterprise'
                                                                                                )}
                                                                                            </button>
                                                                                        ) : (
                                                                                            <div className="d-flex flex-column gap-2">
                                                                                                <div className="text-muted small mb-2">
                                                                                                    <strong>{isEnterpriseGrowth ? 'Enterprise Growth' : 'Enterprise User'}</strong>
                                                                                                </div>
                                                                                                {isEnterpriseGrowth && (
                                                                                                    <button
                                                                                                        className="btn btn-sm btn-warning"
                                                                                                        onClick={(e) => {
                                                                                                            e.stopPropagation();
                                                                                                            if (window.confirm('Move this organization back to Growth? They keep their Growth subscription and lose Enterprise features.')) {
                                                                                                                removeFromEnterpriseGrowth(user);
                                                                                                            }
                                                                                                        }}
                                                                                                        disabled={isUpdating}
                                                                                                    >
                                                                                                        {updatingUserId === user.id ? 'Updating...' : 'Back to Growth'}
                                                                                                    </button>
                                                                                                )}
                                                                                                {!isEnterpriseGrowth && displayOrg?.subscription?.provider === 'manual' && (
                                                                                                    <button
                                                                                                        className="btn btn-sm btn-outline-dark"
                                                                                                        onClick={(e) => {
                                                                                                            e.stopPropagation();
                                                                                                            openBillingDialog(user);
                                                                                                        }}
                                                                                                    >
                                                                                                        <FaDollarSign className="me-1" />
                                                                                                        Enterprise Billing Settings
                                                                                                    </button>
                                                                                                )}
                                                                                                {isEnterpriseGrowth ? null : displayOrg?.subscription?.oldId || displayOrg?.subscription?.oldPriceId ? (
                                                                                                    <button
                                                                                                        className="btn btn-sm btn-warning"
                                                                                                        onClick={(e) => {
                                                                                                            e.stopPropagation();
                                                                                                            if (window.confirm('Are you sure you want to rollback this user to their previous subscription? This action cannot be undone.')) {
                                                                                                                removeFromEnterpriseSubscription(user, 'rollback');
                                                                                                            }
                                                                                                        }}
                                                                                                        disabled={isUpdating}
                                                                    >
                                                                                                        {updatingUserId === user.id ? (
                                                                                                            'Rolling back...'
                                                                                                        ) : (
                                                                                                            'Rollback to Previous'
                                                                                                        )}
                                                                                                    </button>
                                                                                                ) : (
                                                                                                    <button
                                                                                                        className="btn btn-sm btn-danger"
                                                                                                        onClick={(e) => {
                                                                                                            e.stopPropagation();
                                                                                                            if (window.confirm('Are you sure you want to remove this user\'s subscription completely? This action cannot be undone.')) {
                                                                                                                removeFromEnterpriseSubscription(user, 'remove');
                                                                                                            }
                                                                                                        }}
                                                                                                        disabled={isUpdating}
                                                                    >
                                                                                                        {updatingUserId === user.id ? (
                                                                                                            'Removing...'
                                                                                                        ) : (
                                                                                                            'Remove Subscription'
                                                                                                        )}
                                                                                                    </button>
                                                                                                )}
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                ) : (
                                                                                    <span className="text-muted">No organization to update</span>
                                                                                )}

                                                                                {/* Deactivate organization */}
                                                                                {displayOrg?.id && (
                                                                                    displayOrg.active === false ? (
                                                                                        <div className="mt-3 pt-3 border-top text-danger small">
                                                                                            <FaBan className="me-1" />
                                                                                            <strong>Organization deactivated</strong>
                                                                                        </div>
                                                                                    ) : (
                                                                                        <div className="mt-3 pt-3 border-top">
                                                                                            <button
                                                                                                className="btn btn-sm btn-outline-danger w-100"
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    setDeactivateOrgInfo({
                                                                                                        orgId: displayOrg.id,
                                                                                                        orgName: displayOrg.name,
                                                                                                        loanCount: displayOrg.stats?.loans ?? 0,
                                                                                                        subscription: displayOrg.subscription,
                                                                                                    });
                                                                                                }}
                                                                                            >
                                                                                                <FaBan className="me-1" />
                                                                                                Deactivate Organization
                                                                                            </button>
                                                                                        </div>
                                                                                    )
                                                                                )}
                                                                            </div>
                                                                        </div>

                                                                        {/* Partner Referral Assignment */}
                                                                        <div className="mt-4 pt-3 border-top">
                                                                            <h6 className="mb-2">Partner Referral</h6>
                                                                            <p className="text-muted small mb-3">
                                                                                Assign this user to a partner as if they had signed up via the partner link.
                                                                            </p>
                                                                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                                                                <select
                                                                                    className="form-select form-select-sm"
                                                                                    style={{ maxWidth: '180px' }}
                                                                                    value={currentPartnerSlug}
                                                                                    onChange={(e) => {
                                                                                        setPartnerSlugByUserId((prev) => ({
                                                                                            ...prev,
                                                                                            [user.id]: e.target.value,
                                                                                        }));
                                                                                    }}
                                                                                >
                                                                                    {PARTNER_OPTIONS.map((option) => (
                                                                                        <option key={option.slug} value={option.slug}>
                                                                                            {option.label}
                                                                                        </option>
                                                                                    ))}
                                                                                </select>
                                                                                {currentPartnerSlug === CUSTOM_PARTNER_SENTINEL && (
                                                                                    <input
                                                                                        type="text"
                                                                                        className="form-control form-control-sm"
                                                                                        style={{ maxWidth: '180px' }}
                                                                                        placeholder="Enter slug (e.g. acme)"
                                                                                        value={customPartnerSlugByUserId[user.id] || ''}
                                                                                        onChange={(e) => {
                                                                                            const lower = e.target.value.toLowerCase();
                                                                                            setCustomPartnerSlugByUserId((prev) => ({
                                                                                                ...prev,
                                                                                                [user.id]: lower,
                                                                                            }));
                                                                                        }}
                                                                                        onClick={(e) => e.stopPropagation()}
                                                                                    />
                                                                                )}
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-sm btn-primary"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        saveReferralDetails(user);
                                                                                    }}
                                                                                    disabled={savingReferralUserId === user.id}
                                                                                >
                                                                                    {savingReferralUserId === user.id ? 'Saving...' : 'Save'}
                                                                                </button>
                                                                            </div>
                                                                        </div>

                                                                        {/* Invited Organizations Section */}
                                                                        {displayOrg?._allMemberships?.length > 0 && (
                                                                            <div className="mt-4 pt-3 border-top">
                                                                                <h6 className="mb-3">
                                                                                    <FaBuilding className="me-2" />
                                                                                    Other Organizations (Invited)
                                                                                    <span className="badge bg-secondary ms-2">{displayOrg._allMemberships.length}</span>
                                                                                </h6>
                                                                                <div className="table-responsive">
                                                                                    <table className="table table-sm table-bordered mb-0">
                                                                                        <thead className="table-secondary">
                                                                                            <tr>
                                                                                                <th>Organization</th>
                                                                                                <th>Org ID</th>
                                                                                                <th>Role</th>
                                                                                                <th>Status</th>
                                                                                                <th>Joined</th>
                                                                                            </tr>
                                                                                        </thead>
                                                                                        <tbody>
                                                                                            {displayOrg._allMemberships.map((membership) => (
                                                                                                <tr key={membership.membershipId}>
                                                                                                    <td>
                                                                                                        {membership.orgName}
                                                                                                        {!membership.orgActive && (
                                                                                                            <span className="badge bg-danger ms-2" title="This organization has been deactivated">Deactivated</span>
                                                                                                        )}
                                                                                                    </td>
                                                                                                    <td>
                                                                                                        <small className="text-muted">{membership.orgId}</small>
                                                                                                    </td>
                                                                                                    <td>
                                                                                                        <span className="badge bg-secondary">
                                                                                                            {MEMBER_ROLES.find(r => r.value === membership.role)?.name || membership.role || 'N/A'}
                                                                                                        </span>
                                                                                                    </td>
                                                                                                    <td>
                                                                                                        <span className={`badge ${getMemberStatusBadge(membership.status)}`}>
                                                                                                            {membership.status}
                                                                                                        </span>
                                                                                                    </td>
                                                                                                    <td>
                                                                                                        <small>
                                                                                                            {membership.createdAt?.toDate?.()
                                                                                                                ? new Date(membership.createdAt.toDate()).toLocaleDateString()
                                                                                                                : 'N/A'
                                                                                                            }
                                                                                                        </small>
                                                                                                    </td>
                                                                                                </tr>
                                                                                            ))}
                                                                                        </tbody>
                                                                                    </table>
                                                                                </div>
                                                                            </div>
                                                                        )}

                                                                        {/* Organization Members Section */}
                                                                        {displayOrg?.id && (
                                                                            <div className="mt-4 pt-3 border-top">
                                                                                <div className="d-flex justify-content-between align-items-center mb-3">
                                                                                    <h6 className="mb-0">
                                                                                        <FaUserPlus className="me-2" />
                                                                                        Organization Members
                                                                                    </h6>
                                                                                    <button
                                                                                        className="btn btn-sm btn-primary"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            openInviteDialog(displayOrg.id, displayOrg.name);
                                                                                        }}
                                                                                    >
                                                                                        <FaUserPlus className="me-1" />
                                                                                        Invite Member
                                                                                    </button>
                                                                    </div>

                                                                                {loadingMembers.has(displayOrg.id) ? (
                                                                                    <div className="text-center py-3">
                                                                                        <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                                                                                        <span>Loading members...</span>
                                                                                    </div>
                                                                                ) : organizationMembers[displayOrg.id]?.length > 0 ? (
                                                                                    <div className="table-responsive">
                                                                                        <table className="table table-sm table-bordered mb-0">
                                                                                            <thead className="table-secondary">
                                                                                                <tr>
                                                                                                    <th>Email</th>
                                                                                                    <th>Role</th>
                                                                                                    <th>Status</th>
                                                                                                    <th>Is Addy</th>
                                                                                                    <th>Created At</th>
                                                                                                </tr>
                                                                                            </thead>
                                                                                            <tbody>
                                                                                                {organizationMembers[displayOrg.id].map((member) => (
                                                                                                    <tr key={member.id}>
                                                                                                        <td>{member.email}</td>
                                                                                                        <td>
                                                                                                            <span className="badge bg-secondary">
                                                                                                                {MEMBER_ROLES.find(r => r.value === member.role)?.name || member.role || 'N/A'}
                                                                                                            </span>
                                                                                                        </td>
                                                                                                        <td>
                                                                                                            <span className={`badge ${getMemberStatusBadge(member.status)}`}>
                                                                                                                {member.status || 'Unknown'}
                                                                                                            </span>
                                                                                                        </td>
                                                                                                        <td>
                                                                                                            {member.isAddy ? (
                                                                                                                <span className="badge bg-info">Addy Team</span>
                                                                                                            ) : (
                                                                                                                <span className="text-muted">No</span>
                                                                                                            )}
                                                                                                        </td>
                                                                                                        <td>
                                                                                                            <small>
                                                                                                                {member.createdAt?.toDate?.() 
                                                                                                                    ? new Date(member.createdAt.toDate()).toLocaleDateString()
                                                                                                                    : 'N/A'
                                                                                                                }
                                                                                                            </small>
                                                                                                        </td>
                                                                                                    </tr>
                                                                                                ))}
                                                                                            </tbody>
                                                                                        </table>
                                                                                    </div>
                                                                                ) : (
                                                                                    <div className="text-muted text-center py-2">
                                                                                        No members found. Click "Invite Member" to add one.
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {displayedUsers.length === 0 &&
                        (tableSearchTerm.trim() || authProviderFilter !== "all") &&
                        !isSearchingByEmail && (
                        <div className="alert alert-info" role="alert">
                            <FaSearch className="me-2" />
                            No users found with the selected filters. Try a different search term or sign-in method.
                        </div>
                    )}

                    {allUsers.length === 0 && hasLoadedAllUsers && !isLoadingAllUsers && (
                        <div className="alert alert-info" role="alert">
                            No client users found.
                        </div>
                    )}

                    {!hasLoadedAllUsers && !isLoadingAllUsers && (
                        <div className="alert alert-info" role="alert">
                            Loading client users...
                        </div>
                    )}
                </div>
            </div>

            {/* Invite Member Dialog */}
            <InviteMemberDialog
                isOpen={inviteDialogOpen}
                onClose={closeInviteDialog}
                organizationId={inviteDialogOrgId}
                organizationName={inviteDialogOrgName}
                onSuccess={handleInviteSuccess}
                firestore={activeFirestore}
                useDevDb={useDevDb}
            />

            {/* Enterprise Billing Dialog */}
            <EnterpriseBillingDialog
                isOpen={billingDialogOpen}
                onClose={closeBillingDialog}
                organizationData={billingDialogUser?.organizationData}
                firestore={activeFirestore}
                onOrganizationUpdate={handleBillingOrgUpdate}
                useDevDb={useDevDb}
            />

            <EnterpriseBillingDialog
                isOpen={Boolean(activationDialogUser)}
                onClose={() => setActivationDialogUser(null)}
                organizationData={activationDialogUser?.organizationData}
                firestore={activeFirestore}
                onOrganizationUpdate={handleActivationOrgUpdate}
                useDevDb={useDevDb}
                onActivate={(billingUpdates) => updateToEnterpriseSubscriptionAllUsers(activationDialogUser, billingUpdates)}
                isActivating={isUpdating}
            />

            {/* Deactivate Organization Dialog */}
            <DeactivateOrgModal
                isOpen={!!deactivateOrgInfo}
                onClose={() => setDeactivateOrgInfo(null)}
                orgId={deactivateOrgInfo?.orgId}
                orgName={deactivateOrgInfo?.orgName}
                loanCount={deactivateOrgInfo?.loanCount}
                subscription={deactivateOrgInfo?.subscription}
                onConfirm={handleDeactivateOrg}
                isDeactivating={isDeactivatingOrg}
            />
        </div>
    );
};

export default UserPermissionsPage; 